import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Line, LineChart,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import MeasuredBox from 'shared/components/ui/MeasuredBox';
import { useBreadcrumb, useBreadcrumbActions } from 'shared/context/BreadcrumbContext';
import EventsTimeFilter from 'features/activity/components/EventsTimeFilter';
import { presetToISORange } from 'shared/utils/timePresets';
import { formatDuration, durationAxis } from 'shared/utils/formatDuration';
import {
  tableHeaderRowClass, tableHeaderCellClass, tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';
import { getPromptVersionUsage } from 'features/prompts/api/promptsApi';

/**
 * One prompt VERSION's analytics.
 *
 * A page per version rather than every version on one screen: you load the
 * version you care about and want to know what it did. Scoping the request to a
 * single version_hash means the page costs the same whether the prompt has two
 * versions or two hundred.
 *
 * The unit is the INSTANCE — one span, one compile, one LLM call. Not runs: a
 * run can use this version repeatedly or not at all, and can mix several
 * versions, so a run count is neither "times used" nor attributable to one
 * version. There is no agent breakdown for the same reason — a prompt is used
 * at a span, not owned by an agent.
 *
 * Identified by hash throughout. Labels move — `production` may point somewhere
 * else tomorrow — so a page about what a version DID keys on the immutable
 * identity the spans recorded.
 */

const DEFAULT_PRESET = '30d';

const AXIS = { stroke: 'var(--text-muted)', fontSize: 10 };

/**
 * The bucket axis.
 *
 * Buckets are plotted as pre-formatted STRING labels on a category axis, which
 * is what every other chart in this app does (see the dashboard SeriesCard).
 * Feeding recharts epoch milliseconds — whether as a default category axis or
 * as type="number" scale="time" — collapsed the series: every point resolved to
 * within a few pixels of the same x, so the whole line drew on top of itself.
 *
 * That failure hid for a long time because a single-bucket window renders one
 * centred dot, which is indistinguishable from a correct chart. It only shows
 * once there are two buckets — exactly when someone is reading a real trend.
 */
const BUCKET_AXIS = { dataKey: 'label', minTickGap: 16, ...AXIS };
const GRID = 'var(--border-default)';
const TOOLTIP = {
  background: 'var(--surface-raised)',
  border: '1px solid var(--border-default)',
  borderRadius: 8,
  fontSize: 11,
};

/** Bucket start as the axis label — the hour for hourly, the date otherwise. */
function bucketLabel(iso, bucket) {
  const d = new Date(iso);
  return bucket === 'hour'
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function money(n) {
  const v = Number(n || 0);
  if (!v) return '$0';
  return v < 0.01 ? `$${v.toFixed(6)}` : `$${v.toFixed(4)}`;
}
function int(n) { return Number(n || 0).toLocaleString(); }

/**
 * One number. The breakdown sits INLINE beside it rather than on a line below —
 * a hint line only some cards have makes the grid ragged, so every card is
 * exactly two lines and they all stand the same height.
 */
function MetricCard({ label, value, hint, tone }) {
  return (
    <div className="border-t border-[color:var(--border-subtle)] py-3 pr-4">
      <div className="text-[11px] font-medium text-[color:var(--text-muted)]">{label}</div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className={`text-[20px] font-semibold tabular-nums ${tone || 'text-[color:var(--text-primary)]'}`}>{value}</span>
        {hint ? (
          <span className="text-[11.5px] tabular-nums text-[color:var(--text-disabled)]">({hint})</span>
        ) : null}
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="border-t border-[color:var(--border-default)] pt-3">
      <div className="mb-3 text-[13px] font-semibold text-[color:var(--text-primary)]">{title}</div>
      <div className="h-[180px]">{children}</div>
    </div>
  );
}

export default function PromptVersionUsagePage({ promptId, versionHash }) {
  const { team } = useParams();
  const [preset, setPreset] = useState(DEFAULT_PRESET);
  const [range, setRange] = useState(() => presetToISORange(DEFAULT_PRESET));
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const base = `/${encodeURIComponent(team)}/prompts`;
  const shortHash = String(versionHash || '').slice(0, 8);
  const v = data?.version;
  const s = data?.summary;

  useBreadcrumb([
    { label: 'Prompts', to: base },
    { label: data?.prompt?.name || 'Prompt', to: `${base}/${promptId}` },
    // Hash first, version number in brackets: the hash is the identity the spans
    // recorded, "v1" alone is ambiguous across prompts.
    { label: v?.version_no ? `${shortHash} (v${v.version_no})` : shortHash,
      to: `${base}/${promptId}?version=${shortHash}` },
    { label: 'Usage' },
  ]);

  const onTimeFilterChange = useCallback((next) => {
    setPreset(next?.preset || 'custom');
    if (next?.from && next?.to) setRange({ from: next.from, to: next.to });
  }, []);

  useBreadcrumbActions(
    <div className="flex items-center gap-2">
      {data && !v?.version_no ? (
        <span
          className="inline-flex items-center rounded-md border border-[color:var(--border-default)] px-2 py-0.5 text-[11px] font-medium text-[color:var(--text-muted)]"
          title="No longer in the registry. Its recorded traffic is still real."
        >
          Deleted version
        </span>
      ) : null}
      <EventsTimeFilter selectedPreset={preset} onTimeFilterChange={onTimeFilterChange} menuAlign="right" />
    </div>
  );

  const load = useCallback(async () => {
    if (!range?.from || !range?.to) return;
    setLoading(true);
    try {
      setData(await getPromptVersionUsage(promptId, versionHash, range));
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [promptId, versionHash, range]);

  useEffect(() => { load(); }, [load]);

  // Remount key. recharts keeps a chart's resolved series across a data change,
  // so switching window (5 hourly buckets -> 1 daily) left every point pinned at
  // the OLD scale's only position while the axis re-rendered correctly. The
  // symptom is a chart with correct axes and nothing drawn. Keying on the shape
  // of the data forces a fresh mount whenever the bucketing changes.
  const chartKey = `${data?.bucket || '-'}:${(data?.series || []).length}`;

  // Same rule as the cards: instances is a count, everything else is an average
  // per instance. A bucket with twice the traffic would otherwise show twice the
  // tokens and twice the cost while each call behaved identically.
  const chart = useMemo(() => (data?.series || []).map((b) => {
    const n = b.instance_count || 0;
    return {
    t: new Date(b.bucket).getTime(),
    label: bucketLabel(b.bucket, data?.bucket),
    instances: b.instance_count,
    inputTokens: n ? b.input_tokens / n : 0,
    outputTokens: n ? b.output_tokens / n : 0,
    cost: n ? b.total_cost / n : 0,
    avg: b.avg_duration_ms == null ? null : Math.round(b.avg_duration_ms),
    p95: b.p95_duration_ms == null ? null : Math.round(b.p95_duration_ms),
    };
  }), [data]);

  // The latency axis commits to ONE unit, picked from the largest p95 on it.
  // Data stays in raw ms so the tooltip keeps full precision; only the tick
  // labels are converted.
  const lat = useMemo(
    () => durationAxis(Math.max(0, ...chart.map((b) => b.p95 ?? b.avg ?? 0))),
    [chart],
  );



  const errorRate = s && s.instance_count ? (s.error_count / s.instance_count) * 100 : 0;

  // A version that talks more costs more and runs slower. Output tokens per
  // instance is where a prompt regression surfaces first — before cost, before
  // latency, and long before anyone queries the bill.
  const perInstance = useMemo(() => {
    const n = s?.instance_count || 0;
    if (!n) return { cost: 0, input: null, output: null };
    return {
      cost: (s.total_cost || 0) / n,
      input: (s.input_tokens || 0) / n,
      output: (s.output_tokens || 0) / n,
    };
  }, [s]);

  return (
    <main className="flex-1 overflow-auto bg-surface p-4" style={{ fontFamily: 'Inter, sans-serif' }}>
      {error ? (
        <div className="rounded-lg border border-[color:var(--feedback-error)] bg-[color:var(--feedback-error-bg)] px-4 py-3 text-[12.5px] text-[color:var(--feedback-error)]">
          {error}
        </div>
      ) : loading && !data ? (
        <PageLoader label="Loading usage" />
      ) : s && s.instance_count === 0 ? (
        // Zeros would read as "it ran and did nothing", which is a different and
        // much more alarming fact than "it has not run".
        <div className="py-10 text-center text-[12.5px] text-[color:var(--text-muted)]">
          No recorded traffic in this period.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {/* Instances is the only COUNT here. Everything else is an average
                per instance, because a sum mostly measures traffic: a
                production version and a canary never carry the same load, so
                comparing their totals compares their popularity, not their
                behaviour. Totals stay as the secondary line: you are still
                billed for them. */}
            <MetricCard label="Instances" value={int(s.instance_count)} />
            <MetricCard
              label="Tokens (in / out)"
              value={perInstance.input == null
                ? '—'
                : `${perInstance.input.toFixed(1)} / ${perInstance.output.toFixed(1)}`}
              hint={`${int(s.input_tokens + s.output_tokens)} total`}
            />
            <MetricCard label="Cost" value={money(perInstance.cost)}
              hint={`${money(s.total_cost)} total`} />
            <MetricCard label="Avg latency" value={formatDuration(s.avg_duration_ms)} />
            <MetricCard label="p50 latency" value={formatDuration(s.p50_duration_ms)} />
            <MetricCard label="p95 latency" value={formatDuration(s.p95_duration_ms)} />
            <MetricCard
              label="Error rate"
              value={`${errorRate.toFixed(1)}%`}
              hint={`${int(s.error_count)} of ${int(s.instance_count)}`}
              tone={s.error_count > 0 ? 'text-[color:var(--feedback-error)]' : undefined}
            />
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <ChartCard title="Instances">
              <MeasuredBox>
                {({ width, height }) => (
                  <AreaChart key={chartKey} width={width} height={height} data={chart}>
                  <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
                  <XAxis {...BUCKET_AXIS} />
                  <YAxis allowDecimals={false} {...AXIS} />
                  <Tooltip contentStyle={TOOLTIP} />
                  <Area type="monotone" name="Instances" dataKey="instances" stroke="var(--brand-primary)"
                    fill="var(--brand-primary)" fillOpacity={0.15} />
                  </AreaChart>
              )}
              </MeasuredBox>
            </ChartCard>

            <ChartCard title="Latency (avg / p95)">
              <MeasuredBox>
                {({ width, height }) => (
                  <LineChart key={chartKey} width={width} height={height} data={chart}>
                  <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
                  <XAxis {...BUCKET_AXIS} />
                  <YAxis {...AXIS} unit={lat.unit}
                    tickFormatter={(v) => lat.tick(lat.toUnit(v))} />
                  <Tooltip contentStyle={TOOLTIP} 
                    formatter={(v) => formatDuration(v)} />
                  <Line type="monotone" name="Avg" dataKey="avg" stroke="var(--brand-primary)" dot={false} />
                  <Line type="monotone" name="p95" dataKey="p95" stroke="var(--feedback-warning)" dot={false} strokeDasharray="4 3" />
                  </LineChart>
              )}
              </MeasuredBox>
            </ChartCard>

            <ChartCard title="Tokens / instance (in / out)">
              <MeasuredBox>
                {({ width, height }) => (
                  <BarChart key={chartKey} width={width} height={height} data={chart}>
                  <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
                  <XAxis {...BUCKET_AXIS} />
                  <YAxis {...AXIS} />
                  <Tooltip contentStyle={TOOLTIP} />
                  <Bar name="In" dataKey="inputTokens" stackId="t" maxBarSize={48} fill="var(--brand-primary)" fillOpacity={0.5} />
                  <Bar name="Out" dataKey="outputTokens" stackId="t" maxBarSize={48} fill="var(--brand-primary)" />
                  </BarChart>
              )}
              </MeasuredBox>
            </ChartCard>

            <ChartCard title="Cost / instance">
              <MeasuredBox>
                {({ width, height }) => (
                  <AreaChart key={chartKey} width={width} height={height} data={chart}>
                  <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
                  <XAxis {...BUCKET_AXIS} />
                  <YAxis {...AXIS} width={70} tickFormatter={money} />
                  <Tooltip contentStyle={TOOLTIP} formatter={money} />
                  <Area type="monotone" name="Cost" dataKey="cost" stroke="var(--feedback-success)"
                    fill="var(--feedback-success)" fillOpacity={0.15} />
                  </AreaChart>
              )}
              </MeasuredBox>
            </ChartCard>
          </div>

          {/* Cost, latency and errors say CHEAPER and FASTER. Only this says
              BETTER: and a version that is cheap, fast and wrong is a bad
              version. Absent until an evaluator has scored this version's
              traffic, rather than shown as an empty panel implying zero. */}
          {(data.evals || []).length > 0 && (
            <div className="mt-3 overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
              <div className="px-4 py-3 text-[13px] font-semibold text-[color:var(--text-primary)]">
                Eval scores
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className={tableHeaderRowClass}>
                    <th className={tableHeaderCellClass}>Evaluator</th>
                    <th className={`${tableHeaderCellClass} text-right`}>Avg score</th>
                    <th className={`${tableHeaderCellClass} text-right`}>Passed</th>
                    <th className={`${tableHeaderCellClass} text-right`}>Scored</th>
                  </tr>
                </thead>
                <tbody>
                  {data.evals.map((e) => {
                    const rate = e.passed + e.failed
                      ? (e.passed / (e.passed + e.failed)) * 100 : null;
                    return (
                      <tr key={e.evaluator_id} className="h-8 border-b border-[color:var(--border-subtle)] last:border-b-0">
                        <td className={`${tableDataCellClass} max-w-0 truncate`}>{e.evaluator_name}</td>
                        <td className={`${tableDataCellClass} text-right tabular-nums`}>
                          {e.avg_score == null ? '—' : e.avg_score.toFixed(2)}
                        </td>
                        <td className={`${tableDataCellClass} text-right tabular-nums`}>
                          {rate == null ? '—' : `${rate.toFixed(0)}%`}
                        </td>
                        <td className={`${tableDataCellClass} text-right tabular-nums text-[color:var(--text-muted)]`}
                          title={`${e.scored_span} scored on the LLM span directly, ${e.scored_run} on a run that used only this version`}>
                          {int(e.scored)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {s.first_seen && (
            <div className="mt-3 text-[10.5px] text-[color:var(--text-disabled)]">
              First seen {new Date(s.first_seen).toLocaleString()} · last seen {new Date(s.last_seen).toLocaleString()}
            </div>
          )}
        </>
      )}
    </main>
  );
}
